import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import apiClient from "../apiClient";

import type { TokenTransactionClass, User, UserUnlockable, UserSession } from "@/types/models";

const USERS_API_PATH = "/v1/admin/users";

export interface AdminUsersFilter {
	query?: string;
	order?: "asc" | "desc";
	sortBy?: "created_at" | "logged_in_at";
	page?: number;
	limit?: number;
}

export interface UsersResponse {
	users: User[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		total_pages: number;
	};
}

export interface AdminCreateUserBody {
	email: string;
	password?: string;
	first_name?: string;
	last_name?: string;
	date_of_birth?: string;
	gender?: string;
	language?: string;
	expertise_level?: number;
	preferred_currency?: string;
	timezone?: string;
	email_verified?: boolean;
}

export interface SignInReq {
	email: string;
	password: string;
}

const signin = (data: SignInReq) =>
	Promise.resolve({
		user: {
			id: "1",
			email: data.email,
			username: "test",
		},
		accessToken: "test-access-token",
		refreshToken: "test-refresh-token",
	});

const findAll = (filter?: AdminUsersFilter) => {
	const params = new URLSearchParams();
	if (filter?.query) params.append("query", filter.query);
	if (filter?.order) params.append("order", filter.order);
	if (filter?.sortBy) params.append("order_by", filter.sortBy);
	if (filter?.page) params.append("page", filter.page.toString());
	if (filter?.limit) params.append("limit", filter.limit.toString());

	const url = params.toString() ? `${USERS_API_PATH}?${params.toString()}` : USERS_API_PATH;
	return apiClient.get<UsersResponse>({ url });
};
const findById = (id: number) => apiClient.get<User>({ url: `${USERS_API_PATH}/${id}` });
const create = (body: AdminCreateUserBody) => apiClient.post<User>({ url: USERS_API_PATH, data: body });
const findUserUnlockables = (userId: number) =>
	apiClient.get<UserUnlockable[]>({
		url: `${USERS_API_PATH}/${userId}/unlockables`,
	});
const grantTokens = (userId: number, amount: number, tokenClass: TokenTransactionClass = "COMMON") =>
	apiClient.post<User>({
		url: `${USERS_API_PATH}/${userId}/tokens/grant`,
		data: { amount, class: tokenClass },
	});
const grantPermission = (userId: number, permissionId: number) =>
	apiClient.post({
		url: `${USERS_API_PATH}/${userId}/permissions/${permissionId}`,
	});

const revokePermission = (userId: number, permissionId: number) =>
	apiClient.delete({
		url: `${USERS_API_PATH}/${userId}/permissions/${permissionId}`,
	});

export interface UserSessionsParams {
	limit?: number;
	offset?: number;
}

const findUserSessions = (userId: number, params?: UserSessionsParams) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append("limit", params.limit.toString());
	if (params?.offset) queryParams.append("offset", params.offset.toString());

	const url = queryParams.toString()
		? `${USERS_API_PATH}/${userId}/sessions?${queryParams.toString()}`
		: `${USERS_API_PATH}/${userId}/sessions`;
	return apiClient.get<UserSession[]>({ url });
};

export const useUsers = (filter?: AdminUsersFilter) =>
	useQuery({
		queryKey: ["users", filter],
		queryFn: () => findAll(filter),
	});

export const useUser = (id: number) =>
	useQuery({
		queryKey: ["user", id],
		queryFn: () => findById(id),
		enabled: !!id,
	});

export const useUserUnlockables = (userId: number) =>
	useQuery({
		queryKey: ["user", userId, "unlockables"],
		queryFn: () => findUserUnlockables(userId),
		enabled: !!userId,
	});

export const useUserSessions = (userId: number, params?: UserSessionsParams) =>
	useQuery({
		queryKey: ["user", userId, "sessions", params],
		queryFn: () => findUserSessions(userId, params),
		enabled: !!userId,
	});

export const useCreateUser = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: (user) => {
			queryClient.invalidateQueries({ queryKey: ["users"], exact: false });
			if (user?.id) queryClient.setQueryData(["user", user.id], user);
			toast.success("User created");
		},
	});
};

export const useGrantUserTokens = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			userId,
			amount,
			tokenClass = "COMMON",
		}: {
			userId: number;
			amount: number;
			tokenClass?: TokenTransactionClass;
		}) => grantTokens(userId, amount, tokenClass),
		onSuccess: (_, { userId }) => {
			queryClient.invalidateQueries({ queryKey: ["user", userId] });
			queryClient.invalidateQueries({ queryKey: ["users"], exact: false });
		},
	});
};

export default {
	findAll,
	findById,
	findUserUnlockables,
	grantPermission,
	revokePermission,
	grantTokens,
	signin,
};
